import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SiteSearch from "@/components/SiteSearch";
import LanguageSelect from "@/components/LanguageSelect";
import { usePageCrumbs, type Crumb } from "@/lib/pageCrumbs";
import { SECTIONS, centreCrumbName } from "@/lib/sections";

/* section names for the trail: the schema's four, and the guide sections whose
   pages set their own trail but may not yet have when the bar first draws */
const SECTION_LABELS: Record<string, string> = {
  ...SECTIONS,
  "country-guide": "Country Guides",
  "ayush-visa": "AYUSH Visa",
  "patient-testimonials": "Patient Testimonials",
};

/** The page's own heading: its <h1>, or on a page titled with an <h2>, that. */
function pageHeading(): string {
  // innerText, not textContent: an h1 set in lines (block spans) reads with a space between them
  const text = (el: Element | null | undefined) => ((el as HTMLElement | null)?.innerText || el?.textContent || "").replace(/\s+/g, " ").trim();
  const h1 = text(document.querySelector("h1"));
  if (h1) return h1;
  return text([...document.querySelectorAll("h2")].find((el) => !el.closest("nav, header, footer")));
}

/** Reads the heading once the page has drawn it (lazy sections, fetched content arrive late). */
function usePageHeading(pathname: string, wanted: boolean): string {
  const [heading, setHeading] = useState("");
  useEffect(() => {
    setHeading("");
    if (!wanted) return;
    const found = () => {
      const h = pageHeading();
      if (h) setHeading(h);
      return !!h;
    };
    if (found()) return;
    const watch = new MutationObserver(() => {
      if (found()) watch.disconnect();
    });
    watch.observe(document.body, { childList: true, subtree: true });
    const giveUp = window.setTimeout(() => {
      watch.disconnect();
      if (!pageHeading()) setHeading(document.title.replace(/\s*[|–—-]\s*MyVaidyam.*$/i, "").trim());
    }, 3000);
    return () => {
      watch.disconnect();
      window.clearTimeout(giveUp);
    };
  }, [pathname, wanted]);
  return heading;
}

function Crumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-2 overflow-x-auto whitespace-nowrap text-[13px] font-medium md:text-sm [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((c, i) => {
          const last = i === items.length - 1;
          return (
            <li key={i} className="flex shrink-0 items-center gap-2">
              {c.to && !last ? (
                <Link to={c.to} className="text-[#336278] transition-colors hover:text-[#2C4E5A] hover:underline">
                  {c.label}
                </Link>
              ) : (
                <span aria-current={last ? "page" : undefined} className="font-semibold text-[#2C4E5A]">
                  {c.label}
                </span>
              )}
              {!last && <ChevronRight className="h-3.5 w-3.5 text-[#2C4E5A]/40" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The bar under the header on every page: the breadcrumb on the left, the
 * site search and the language picker on the right. A page sets its own
 * trail (TestimonialsBreadcrumb / PageBreadcrumb with items); otherwise the
 * trail is Home, the URL's section and the page's heading. The home page has
 * no trail, only the search and the picker.
 */
export default function SiteBar() {
  const { pathname } = useLocation();
  const set = usePageCrumbs();
  const isHome = pathname === "/";
  const heading = usePageHeading(pathname, !set && !isHome);

  let trail: Crumb[] = [];
  if (set) trail = set;
  else if (!isHome) {
    const [section, ...rest] = pathname.split("/").filter(Boolean);
    trail = [{ label: "Home", to: "/" }];
    if (rest.length && SECTION_LABELS[section]) trail.push({ label: SECTION_LABELS[section], to: `/${section}` });
    // a centre is named from its URL (lib/sections), the rest by their heading
    if (heading) trail.push({ label: centreCrumbName(pathname, heading) ?? heading });
  }

  return (
    <div className="border-b border-[#2C4E5A]/10 bg-[#EEF4F5]">
      <div className="container mx-auto flex flex-col gap-2 px-4 py-2 md:flex-row md:items-center md:justify-between md:gap-6">
        {trail.length > 0 ? <Crumbs items={trail} /> : <span className="hidden md:block" />}
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 md:w-[20rem] md:flex-none lg:w-[24rem]">
            <SiteSearch />
          </div>
          <LanguageSelect />
        </div>
      </div>
    </div>
  );
}
